import { call, put, select } from 'redux-saga/effects';
import {
    loginUserFailure,
    requestNewPasswordFailure,
    sendNewPasswordFailure,
    userLoggedIn,
    userLoggedOut,
} from '../actions/security';
import Security from '../services/api/Security';
import history from '../history';
import { loadingStart, loadingStop } from '../actions/loadingState';
import {
    completeAuthentication,
    sendNewPasswordAccessToken,
} from '../actions/routines';
import { fetchUserDetailsSaga } from './userDetailsSaga';
import { getLandingPath } from '../services/landingPath';

/**
 * Shared tail of every authentication flow. The password grant calls it
 * directly; UpFresh SSO and login-as-demo reach it through
 * `/auth/token/:token/:refreshToken` (see `components/Security/TokenAuth`),
 * which dispatches the routine. Both arrive with the same token pair, so the
 * landing decision is made in exactly one place.
 *
 * The user is loaded *before* `userLoggedIn` is dispatched: the moment the
 * store flips to authenticated, `SecurityRoute` renders its own redirect for
 * anyone still sitting on the login form, and it renders first. Both read
 * `getLandingPath`, so they agree — but only if the user is already in the
 * store by then.
 */
export function* completeAuthenticationSaga(action) {
    const { tokens } = action.payload;

    localStorage.token = tokens.access_token;
    localStorage.refreshToken = tokens.refresh_token;

    yield put(completeAuthentication.request());

    // Cannot throw: fetchUserDetailsSaga turns failures into
    // fetchUserData.failure, which leaves the flags below undefined and lands
    // the user on the dashboard.
    yield call(fetchUserDetailsSaga);

    const userDetails = yield select((state) => state.userDetails.data);
    const landingPath = getLandingPath(userDetails);

    yield put(userLoggedIn(tokens));
    // replace, not push: the entry being left behind is the SSO hand-off, and
    // the back button has no business returning to it.
    history.replace(landingPath);

    yield put(completeAuthentication.success({ landingPath }));
    yield put(completeAuthentication.fulfill());
}

export function* loginUserSaga(action) {
    yield put(loadingStart());
    try {
        const token = yield call(Security.login, action.data);

        if (token.data.need_password_reset) {
            localStorage.token = token.data.access_token;
            localStorage.refreshToken = token.data.refresh_token;
            localStorage.needPasswordReset = 1;
            yield put(userLoggedIn(token.data));
            history.push('/security/force-new-password');
        } else {
            yield call(
                completeAuthenticationSaga,
                completeAuthentication({ tokens: token.data })
            );
        }
    } catch (err) {
        yield put(loginUserFailure(err.response.data.error_description));
    }
    yield put(loadingStop());
}

export function* logoutUserSaga() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    yield put(userLoggedOut());
    //history.push('/');
    document.location = '/';
}

export function* requestNewPasswordSaga(action) {
    yield put(loadingStart());
    try {
        yield call(Security.requestNewPassword, action.login);
        history.push('/security/password-recovery/check-email');
    } catch (err) {
        yield put(requestNewPasswordFailure(err.response.data.error));
    }
    yield put(loadingStop());
}

export function* sendNewPasswordResetTokenSaga(action) {
    yield put(loadingStart());
    try {
        yield call(Security.sendNewPasswordResetToken, action.data);
        history.push('/');
    } catch (err) {
        yield put(sendNewPasswordFailure(err.response.data.error_description));
    }
    yield put(loadingStop());
}

export function* sendNewPasswordAccessTokenSaga(action) {
    try {
        yield put(sendNewPasswordAccessToken.request());

        const response = yield call(
            Security.sendNewPasswordAccessToken,
            action.payload
        );

        yield put(sendNewPasswordAccessToken.success(response.data));
    } catch (error) {
        yield put(sendNewPasswordAccessToken.failure(error.message));
    } finally {
        yield put(sendNewPasswordAccessToken.fulfill());
    }
}
